import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import authService from "../../services/auth.service";
import useReveal from "../../components/useReveal";
import { GoogleIcon, EyeIcon, EyeOffIcon } from "../../components/auth/AuthIcons";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "../../config/storeLinks";
import PasswordStrength from "./components/PasswordStrength";

export default function SignUpPageView() {
  useReveal();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "developer" ? "developer" : "individual";
  const setTab = (t) => setSearchParams({ tab: t }, { replace: true });

  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const pwdRules = [
    { label: "At least 8 characters",          test: (p) => p.length >= 8 },
    { label: "One uppercase letter",            test: (p) => /[A-Z]/.test(p) },
    { label: "One lowercase letter",            test: (p) => /[a-z]/.test(p) },
    { label: "One number",                      test: (p) => /[0-9]/.test(p) },
    { label: "One special character (!@#$ …)",  test: (p) => /[^A-Za-z0-9]/.test(p) },
  ];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  /* ── Email / password sign-up ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.password) {
      setError("Please fill in all required fields."); return;
    }
    const failedRule = pwdRules.find((r) => !r.test(form.password));
    if (failedRule) {
      setError(failedRule.label.startsWith("At") ? "Password must be at least 8 characters." : `Password must contain: ${failedRule.label.toLowerCase()}.`);
      return;
    }
    setLoading(true);
    try {
      await authService.signUpEmail({ name: form.name, email: form.email, password: form.password, company: form.company });
      navigate("/dashboard");
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        // Account exists (e.g. mobile app user) — sign them in instead
        try {
          await authService.signInEmail({ email: form.email, password: form.password, name: form.name, company: form.company });
          navigate("/dashboard");
        } catch (signInErr) {
          if (signInErr.code === "auth/wrong-password" || signInErr.code === "auth/invalid-credential") {
            navigate("/login?email=" + encodeURIComponent(form.email));
          } else {
            setError(signInErr.message);
          }
        }
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Google sign-in ── */
  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await authService.signInGoogle();
      navigate("/dashboard");
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") return;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    background: "var(--bg-surface)",
    border: "1px solid var(--border-light)",
    borderRadius: 12,
    color: "var(--text-primary)",
    fontSize: 15,
    fontFamily: "inherit",
    outline: "none",
    transition: "border-color .2s",
  };

  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-secondary)",
    marginBottom: 8,
    letterSpacing: "-.01em",
  };

  /* ── Tab styles ── */
  const tabBar = {
    display: "flex", gap: 4,
    background: "var(--bg-surface)",
    border: "1px solid var(--border-subtle)",
    borderRadius: 12, padding: 4, marginBottom: 40,
  };
  const tabBtn = (active) => ({
    flex: 1, padding: "9px 0",
    fontSize: 14, fontWeight: 600, fontFamily: "inherit",
    border: "none", outline: "none", borderRadius: 9, cursor: "pointer",
    transition: "background .2s, color .2s, box-shadow .2s",
    background: active ? "var(--bg-card)" : "transparent",
    color: active ? "var(--text-primary)" : "var(--text-tertiary)",
    boxShadow: active ? "0 1px 4px rgba(0,0,0,.12)" : "none",
  });

  return (
    <div style={{ background: "var(--bg-primary)", paddingTop: 80, minHeight: "100vh" }}>
      <section className="section section-dark">
        <div style={{ maxWidth: 440, margin: "0 auto", padding: "0 2rem" }}>

          {/* ── Header ── */}
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div className="reveal eyebrow">Get started</div>
            <h1 className="reveal headline" style={{ fontSize: "clamp(32px,5vw,48px)", marginBottom: 12 }}>Join Ateno</h1>
            <p className="reveal subhead" style={{ fontSize: 16 }}>
              Get started with the Ateno Studio mobile app.
            </p>
          </div>

          {/* ── Tab switcher ── */}
          <div className="reveal" style={tabBar}>
            <button style={tabBtn(tab === "individual")} onClick={() => setTab("individual")}>Individual</button>
            <button style={tabBtn(tab === "developer")} onClick={() => setTab("developer")}>Developer</button>
          </div>

          {/* ══ Individual tab ══ */}
          {tab === "individual" && (
            <div className="reveal" style={{ textAlign: "center", padding: "8px 0 24px" }}>
              <h2 style={{ fontSize: "clamp(22px,4vw,32px)", fontWeight: 700, letterSpacing: "-.02em", marginBottom: 12, color: "var(--text-primary)" }}>
                Get Ateno Studio
              </h2>
              <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.65, maxWidth: 340, margin: "0 auto 40px" }}>
                Download the mobile app on iOS or Android.
              </p>

              {/* App store buttons */}
              <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap", marginBottom: 40 }}>
                {/* App Store */}
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-badge-link"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 10,
                    borderRadius: 12, padding: "10px 20px",
                    textDecoration: "none", transition: "opacity .2s, box-shadow .2s",
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div style={{ textAlign: "left", lineHeight: 1.2 }}>
                    <div style={{ fontSize: 10, opacity: 0.85, fontWeight: 400 }}>Download on the</div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>App Store</div>
                  </div>
                </a>

                {/* Google Play */}
                <a
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-badge-link"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 10,
                    borderRadius: 12, padding: "10px 20px",
                    textDecoration: "none", transition: "opacity .2s, box-shadow .2s",
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 26 24" fill="none">
                    <path d="M3.18 23.5c-.34-.19-.55-.56-.55-1.06V1.56c0-.5.21-.87.55-1.06l.12-.06 12.27 12.27v.29L3.3 23.56l-.12-.06Z" fill="#EA4335"/>
                    <path d="M19.63 16.45l-4.06-4.06v-.29l4.06-4.06.09.05 4.81 2.73c1.37.78 1.37 2.05 0 2.83l-4.81 2.74-.09.06Z" fill="#FBBC05"/>
                    <path d="M19.72 16.39 15.57 12.2 3.18 23.5c.45.48 1.19.54 2.02.06l14.52-7.17Z" fill="#34A853"/>
                    <path d="M19.72 8.01 5.2.84C4.37.36 3.63.42 3.18.9l12.39 11.3 4.15-4.19Z" fill="#4285F4"/>
                  </svg>
                  <div style={{ textAlign: "left", lineHeight: 1.2 }}>
                    <div style={{ fontSize: 10, opacity: 0.85, fontWeight: 400 }}>GET IT ON</div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>Google Play</div>
                  </div>
                </a>
              </div>


            </div>
          )}

          {/* ══ Developer tab ══ */}
          {tab === "developer" && (
            <div className="reveal" style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              <button
                type="button"
                onClick={handleGoogle}
                disabled={loading}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  width: "100%", padding: "12px 16px",
                  background: "#fff", color: "#1f1f1f", fontWeight: 600, fontSize: 15, fontFamily: "inherit",
                  border: "1px solid var(--border-color)", borderRadius: 12, cursor: "pointer", transition: "opacity .2s",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <GoogleIcon /> Continue with Google
              </button>

              {/* ── Divider ── */}
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ flex: 1, height: 1, background: "var(--border-light)" }} />
                <span style={{ fontSize: 12, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>or</span>
                <div style={{ flex: 1, height: 1, background: "var(--border-light)" }} />
              </div>

              {/* ── Email / password form ── */}
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div>
                  <label style={labelStyle}>Full name *</label>
                  <input type="text" value={form.name} onChange={set("name")} placeholder="Jane Smith" style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
                    onBlur={(e) => (e.target.style.borderColor = "var(--border-light)")} />
                </div>
                <div>
                  <label style={labelStyle}>Email address *</label>
                  <input type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
                    onBlur={(e) => (e.target.style.borderColor = "var(--border-light)")} />
                </div>
                <div>
                  <label style={labelStyle}>Password *</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPwd ? "text" : "password"}
                      value={form.password}
                      onChange={set("password")}
                      placeholder="At least 8 characters"
                      style={{ ...inputStyle, paddingRight: "40px" }}
                      onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
                      onBlur={(e) => (e.target.style.borderColor = "var(--border-light)")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: 14,
                        transform: "translateY(-50%)",
                        width: 20,
                        height: 20,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        color: "var(--text-tertiary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 0,
                      }}
                      tabIndex={-1}
                    >
                      {showPwd ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  <PasswordStrength password={form.password} rules={pwdRules} />
                </div>
                <div>
                  <label style={labelStyle}>Company <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>(optional)</span></label>
                  <input type="text" value={form.company} onChange={set("company")} placeholder="Acme Inc." style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
                    onBlur={(e) => (e.target.style.borderColor = "var(--border-light)")} />
                </div>

                {error && (
                  <div style={{ background: "rgba(248,113,113,.1)", border: "1px solid rgba(248,113,113,.25)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#f87171" }}>
                    {error}
                  </div>
                )}

                <button type="submit" className="btn btn-white btn-lg btn-full" disabled={loading}
                  style={{ marginTop: 4, opacity: loading ? 0.7 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  {loading ? (
                    <>
                      <svg
                        width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                        style={{ animation: "spin .75s linear infinite", flexShrink: 0 }}
                      >
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                      Creating account…
                    </>
                  ) : "Create account"}
                </button>
              </form>

              <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.6 }}>
                Already have an account?{" "}
                <Link to="/login" style={{ color: "#818cf8", textDecoration: "none" }}>Sign in</Link>
              </p>

              <p style={{ fontSize: 11, color: "var(--text-faint)", textAlign: "center", lineHeight: 1.6 }}>
                By signing up you agree to our{" "}
                <Link to="/terms" style={{ color: "var(--text-muted)", textDecoration: "none" }}>Terms of Service</Link>{" "}
                and{" "}
                <Link to="/privacy" style={{ color: "var(--text-muted)", textDecoration: "none" }}>Privacy Policy</Link>.
              </p>
            </div>
          )}

        </div>
      </section>
    </div>
  );
}
